import React, { useEffect, useState, useMemo } from 'react';
import {
	Form,
	InputNumber,
	Button,
	Input,
	Row,
	Col,
	Typography,
	Select,
	message,
	FormInstance,
	Tooltip,
} from 'antd';
import {
	PlusOutlined,
	DeleteOutlined,
	CheckOutlined,
	EditOutlined,
	InfoCircleOutlined,
	MinusOutlined,
} from '@ant-design/icons';
import { DotSeparator } from '../common/FormDots';
import LaborTimeSelect, { TIME_IN } from '../supplier_invoice_form/LaborTimeSelect';
import ParsedFieldIndicator from '../InvoiceFormBase/ParsedFieldIndicator';
import { useParsedFieldIndicator } from '../InvoiceFormBase/useParsedFieldIndicator';
import { nullSafeGet } from '../../utils/DataAccessUtils';

const { Option } = Select;

const FORM_INIT_DELAY_MS = 100;
const TOTAL_UPDATE_DELAY_MS = 0;
const DEFAULT_AMOUNT = 0;
const DEFAULT_TIME_UNIT = 'Hours';
const COL_SPAN_FIELDS = 14;
const COL_SPAN_ACTIONS = 10;
const COL_SPAN_FIELD_INPUT = 12;
const COL_SPAN_LINE_ITEM_DESC = 14;
const COL_SPAN_LINE_ITEM_ACTIONS = 10;
const INPUT_WIDTH = 140;
const MAX_TIME_VALUE = 1000;
const MIN_TIME_MINUTES = 1;
const MIN_TIME_HOURS = 0.1;

export interface FieldConfig {
	key: string;
	label: string;
	type: 'text' | 'number' | 'select' | 'time';
	placeholder?: string;
	required?: boolean;
	min?: number;
	max?: number;
	step?: number;
	options?: { label: string; value: any }[];
	validationRules?: any[];
}

export interface CostSectionConfig {
	label: string;
	fields: FieldConfig[];
	calculateAmount: (item: any) => number;
	getDisplayText: (item: any, currency: any) => string;
	getDefaultItem: () => any;
}

interface CurrencyType {
	id: string;
	format: (value: string | number) => string;
}

interface CostSectionProps {
	label: string;
	form: FormInstance;
	fieldKey: string;
	currency: CurrencyType;
	config: CostSectionConfig;
	onTotalChange?: () => void;
	parsedDataSnapshot?: any;
	disabled?: boolean;
}

type SectionMode = 'total' | 'line-items';

const CostSection: React.FC<CostSectionProps> = ({
	label,
	form,
	fieldKey,
	currency,
	config,
	onTotalChange,
	parsedDataSnapshot,
	disabled = false,
}) => {
	const { getIndicator } = useParsedFieldIndicator({
		parsedDataSnapshot,
		formInstance: form,
	});
	const [mode, setMode] = useState<SectionMode>('total');
	const [lineItems, setLineItems] = useState<any[]>([]);
	const [timeIn, setTimeIn] = useState<string>(DEFAULT_TIME_UNIT);
	const [isEditing, setIsEditing] = useState<number | null>(null);

	const totalFieldName = useMemo(() => `${fieldKey}Total`, [fieldKey]);
	const lineItemsFieldName = useMemo(() => `${fieldKey}LineItems`, [fieldKey]);

	// Initialize line items from form data if available
	useEffect(() => {
		const timer = setTimeout(() => {
			const initialLineItems = form.getFieldValue(lineItemsFieldName);

			if (initialLineItems && initialLineItems.length > 0) {
				const processedLineItems = initialLineItems.map((item) => {
					const existingAmount = Number(item.amount);
					const calculatedAmount =
						existingAmount && existingAmount > 0 ? existingAmount : config.calculateAmount(item);

					return {
						...item,
						amount: calculatedAmount,
					};
				});

				setLineItems(processedLineItems);
				setMode('line-items');
			}
		}, FORM_INIT_DELAY_MS);

		return () => clearTimeout(timer);
	}, [form, lineItemsFieldName, config]);

	useEffect(() => {
		if (mode === 'line-items') {
			const newTotal = lineItems.reduce(
				(sum, item) => sum + (Number(item.amount) || DEFAULT_AMOUNT),
				DEFAULT_AMOUNT
			);
			form.setFieldsValue({
				[totalFieldName]: newTotal,
			});
			if (onTotalChange) {
				setTimeout(() => onTotalChange(), TOTAL_UPDATE_DELAY_MS);
			}
		}
	}, [lineItems, totalFieldName, form, onTotalChange, mode]);

	useEffect(() => {
		if (lineItems.length === 0) return;

		const updated = lineItems.map((item) => ({
			...item,
			amount: config.calculateAmount(item),
		}));
		setLineItems(updated);
		form.setFieldsValue({ [lineItemsFieldName]: updated });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [timeIn]);

	useEffect(() => {
		if (mode === 'line-items' && lineItems.length === 0) {
			setMode('total');
			form.setFieldsValue({
				[lineItemsFieldName]: undefined,
				[totalFieldName]: undefined,
			});
		}
	}, [lineItems, mode, form, lineItemsFieldName, totalFieldName]);

	const handleAddLineItem = () => {
		const newItem = {
			...config.getDefaultItem(),
			amount: DEFAULT_AMOUNT,
		};

		setLineItems([...lineItems, newItem]);
		setMode('line-items');
		setIsEditing(lineItems.length);
	};

	const handleLineItemChange = (index: number, key: string, value: any) => {
		const updated = [...lineItems];
		updated[index][key] = value;
		updated[index].amount = config.calculateAmount(updated[index]);

		setLineItems(updated);
		form.setFieldsValue({ [lineItemsFieldName]: updated });
	};

	const handleRemoveLineItem = (index: number) => {
		const updated = [...lineItems];
		updated.splice(index, 1);
		setLineItems(updated);
		form.setFieldsValue({ [lineItemsFieldName]: updated });
		if (isEditing === index) {
			setIsEditing(null);
		} else if (isEditing !== null && isEditing > index) {
			setIsEditing(isEditing - 1);
		}
	};

	const isLineItemValid = (index: number) => {
		const item = lineItems[index];
		if (!item) return false;

		return config.fields
			.filter((field) => field.required !== false)
			.every((field) => {
				const value = item[field.key];
				return value !== undefined && value !== null && value !== '';
			});
	};

	const validateLineItem = async (index: number) => {
		try {
			const fieldNames = config.fields.map(
				(field) => `${lineItemsFieldName}[${index}].${field.key}`
			);
			await form.validateFields(fieldNames);
			setIsEditing(null);
		} catch (error) {
			message.error('Please fix validation errors before proceeding');
		}
	};

	const getParsedLineItemValue = (index: number, fieldKey: string) => {
		if (!parsedDataSnapshot) return undefined;

		const sectionMapping: Record<string, string> = {
			labor: 'laborLineItems',
			parts: 'materialLineItems',
			equipment: 'materialLineItems',
			travel: 'travelLineItems',
			freight: 'freightLineItems',
			misc: 'miscLineItems',
		};

		const parsedArrayKey = sectionMapping[fieldKey];
		if (!parsedArrayKey || !parsedDataSnapshot[parsedArrayKey]) return undefined;

		const parsedLineItems = parsedDataSnapshot[parsedArrayKey];

		let filteredItems = parsedLineItems;
		if (fieldKey === 'parts') {
			filteredItems = parsedLineItems.filter((item: any) => item.partId);
		} else if (fieldKey === 'equipment') {
			filteredItems = parsedLineItems.filter((item: any) => item.equipmentId);
		}

		if (index >= filteredItems.length) return undefined;

		return filteredItems[index];
	};

	const renderField = (field: FieldConfig, index: number, item: any) => {
		const fieldName = `${lineItemsFieldName}[${index}].${field.key}`;
		let initialValue = item[field.key];

		if (field.type === 'time' && initialValue) {
			const seconds = Number(initialValue);
			if (timeIn === TIME_IN.MINUTES) {
				initialValue = seconds / 60;
			} else {
				initialValue = seconds / 3600;
			}
		}

		switch (field.type) {
			case 'text':
				return (
					<Form.Item
						label={field.label}
						name={fieldName}
						validateTrigger={['onBlur', 'onChange']}
						initialValue={initialValue}
						rules={
							field.validationRules || [
								{
									required: field.required !== false,
									whitespace: true,
									message: `Please add ${field.label.toLowerCase()}.`,
								},
							]
						}
					>
						<Input
							size="small"
							placeholder={field.placeholder}
							onChange={(e) => handleLineItemChange(index, field.key, e.target.value)}
							disabled={disabled}
						/>
					</Form.Item>
				);

			case 'number':
				return (
					<Form.Item
						label={field.label}
						name={fieldName}
						validateTrigger={['onBlur', 'onChange']}
						initialValue={initialValue}
						rules={
							field.validationRules || [
								{ required: field.required !== false, message: 'Please add value.' },
								{
									type: 'number',
									min: field.min || 0,
									message: 'Value must be at least ' + (field.min || 0) + '.',
								},
							]
						}
					>
						<InputNumber
							size="small"
							placeholder={field.placeholder}
							min={field.min}
							max={field.max}
							step={field.step || 1}
							onChange={(val) =>
								handleLineItemChange(
									index,
									field.key,
									val !== null && val !== undefined ? val : null
								)
							}
							style={{ width: '100%' }}
							disabled={disabled}
						/>
					</Form.Item>
				);

			case 'select':
				return (
					<Form.Item
						label={field.label}
						name={fieldName}
						validateTrigger={['onBlur', 'onChange']}
						initialValue={initialValue}
						rules={
							field.validationRules || [
								{
									required: field.required !== false,
									message: `Please select ${field.label.toLowerCase()}.`,
								},
							]
						}
					>
						<Select
							size="small"
							placeholder={field.placeholder}
							onChange={(val) => handleLineItemChange(index, field.key, val)}
							disabled={disabled}
						>
							{field.options &&
								field.options.map((option) => (
									<Option key={option.value} value={option.value}>
										{option.label}
									</Option>
								))}
						</Select>
					</Form.Item>
				);

			case 'time':
				const minTime = timeIn === TIME_IN.MINUTES ? MIN_TIME_MINUTES : MIN_TIME_HOURS;
				return (
					<Form.Item
						label={<LaborTimeSelect value={timeIn} onChange={setTimeIn} />}
						name={fieldName}
						initialValue={initialValue}
						validateTrigger={['onBlur', 'onChange']}
						rules={
							field.validationRules || [
								{ required: field.required !== false, message: 'Please add time quantity.' },
								{
									type: 'number',
									min: minTime,
									message: `Time must be at least ${
										timeIn === TIME_IN.MINUTES
											? `${MIN_TIME_MINUTES} minute`
											: `${MIN_TIME_HOURS} hour`
									}.`,
								},
								{
									type: 'number',
									max: MAX_TIME_VALUE,
									message: `Time cannot exceed ${MAX_TIME_VALUE}.`,
								},
							]
						}
					>
						<InputNumber
							size="small"
							placeholder={timeIn === TIME_IN.MINUTES ? 'Minutes' : 'Hours'}
							min={minTime}
							max={MAX_TIME_VALUE}
							step={timeIn === TIME_IN.MINUTES ? MIN_TIME_MINUTES : MIN_TIME_HOURS}
							onChange={(val) => {
								if (val === null || val === undefined) {
									handleLineItemChange(index, field.key, null);
									return;
								}
								let seconds = val;
								if (timeIn === TIME_IN.MINUTES) {
									seconds = val * 60;
								} else if (timeIn === TIME_IN.HOURS) {
									seconds = val * 3600;
								}
								handleLineItemChange(index, field.key, seconds);
							}}
							style={{ width: '100%' }}
							disabled={disabled}
						/>
					</Form.Item>
				);

			default:
				return null;
		}
	};

	return (
		<div className=" py-6" style={{ borderBottom: '1px solid #F0F0F0' }}>
			<div className=" flex flex-row items-center">
				<div className="h5medium">{label}</div>
				<Button type="link" onClick={handleAddLineItem} style={{ height: 32 }} disabled={disabled}>
					<PlusOutlined /> Add
				</Button>

				{mode === 'total' ? (
					<>
						<DotSeparator />
						<div className="flex items-center gap-2 pl-4">
							{getIndicator(totalFieldName)}
							<Form.Item
								name={totalFieldName}
								rules={[{ required: true, message: 'Enter amount' }]}
								initialValue={0}
								style={{ marginBottom: 0, marginTop: 0 }}
							>
								<InputNumber
									size="small"
									placeholder={`Enter ${label} cost`}
									step={1}
									style={{ width: INPUT_WIDTH, textAlign: 'right' }}
									addonAfter={currency.id}
									disabled={disabled}
								/>
							</Form.Item>
						</div>
					</>
				) : (
					<div></div>
				)}
			</div>

			{mode === 'line-items' && (
				<div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} className="mt-4">
					{lineItems.map((item, index) =>
						isEditing === index ? (
							<Row key={index} style={{ background: '#FAFAFA' }} className="py-4" gutter={24}>
								<Col span={COL_SPAN_FIELDS} style={{ borderRight: '1px solid #E0E0E0' }}>
									<Row gutter={16}>
										{config.fields.map((field) => (
											<Col span={COL_SPAN_FIELD_INPUT} key={field.key}>
												{renderField(field, index, item)}
											</Col>
										))}
									</Row>
								</Col>

								<Col span={COL_SPAN_ACTIONS}>
									<div className="flex flex-row items-center justify-end gap-x-4">
										<span className="bodybold">
											{currency.format((Number(item.amount) || 0).toFixed(currency.decimals))}
										</span>

										{lineItems.length > 0 && (
											<div className="flex flex-row items-center gap-x-2">
												<Button
													icon={<CheckOutlined />}
													type="primary"
													disabled={!isLineItemValid(index) || disabled}
													onClick={() => validateLineItem(index)}
													title={
														isLineItemValid(index) && !disabled
															? 'Validate and save line item'
															: 'Please fill all required fields correctly'
													}
												/>
												<Button
													icon={<DeleteOutlined />}
													type="ghost"
													onClick={() => handleRemoveLineItem(index)}
													disabled={disabled}
												/>
											</div>
										)}
									</div>
								</Col>
							</Row>
						) : (
							<div
								key={index}
								className="ml-3 flex flex-row items-baseline justify-between gap-x-4  "
							>
								<div className="flex flex-row flex-nowrap items-center justify-end gap-x-2 whitespace-pre-line break-words">
									<span className="lineItemDetailText whitespace-pre-line break-words">
										{config.getDisplayText(item, currency)}
									</span>
								</div>
								<DotSeparator />

								<div className="flex flex-row flex-nowrap items-center justify-end gap-x-2">
									<Button
										className="shrink-0"
										icon={<EditOutlined />}
										onClick={() => setIsEditing(index)}
										disabled={disabled}
										type="text"
									/>
									<Button
										className="shrink-0"
										icon={<DeleteOutlined />}
										onClick={() => handleRemoveLineItem(index)}
										disabled={disabled}
										type="text"
									/>
									{item.discount_amount && Number(item.discount_amount) > 0 ? (
										<div className="flex flex-row items-center gap-x-2">
											<span className="footnoteMedium inline-flex items-center gap-1 text-[#237804]">
												<MinusOutlined /> {Number(item.discount_amount).toFixed(currency.decimals)}
											</span>
											<Tooltip
												title={`We detected a total discount of ${Number(
													nullSafeGet('AIData.discount', parsedDataSnapshot)
												).toFixed(currency.decimals)} (${(
													nullSafeGet('AIData.discount_percentage', parsedDataSnapshot) * 100
												).toFixed(2)}%) and applied it on each line item`}
											>
												<InfoCircleOutlined />
											</Tooltip>
										</div>
									) : null}

									{parsedDataSnapshot && (
										<ParsedFieldIndicator
											fieldName={`${fieldKey}[${index}]`}
											currentValue={item}
											parsedValue={getParsedLineItemValue(index, fieldKey)}
											show={true}
										/>
									)}
									<span>
										<InputNumber
											size="small"
											disabled
											step={1}
											precision={currency.decimals}
											style={{ textAlign: 'right', width: INPUT_WIDTH }}
											addonAfter={currency.id}
											value={Number(item.amount) || DEFAULT_AMOUNT}
										/>
									</span>
								</div>
							</div>
						)
					)}
				</div>
			)}
		</div>
	);
};

export default CostSection;
